import { useState, useCallback, useEffect, useRef } from 'react';
import { useAccount } from 'wagmi';
import { useAppContext } from '../contexts/hooks';
import { config } from '../config.ts';
import { useEthersSigner } from './useEthersSigner.ts';
import { useSolanaSigner } from './useSolanaSigner.ts';
import { retry } from '../lib/retry.ts';
import { captureException } from '../sentry';
import {
  generateKeyPair, saveKeyPair, loadKeyPair,
  recoverAddOrderlyKeySigner,
  signRegistration, signAddOrderlyKey,
  signSolanaRegistration, signSolanaAddOrderlyKey,
} from '../services/auth.ts';
import * as api from '../services/orderly-api.ts';
import type { OrderlyChainType } from '../services/orderly-api.ts';
import type { AsyncStatus } from '../types/index.ts';

function addressesMatch(chain: 'evm' | 'solana', a: string, b: string) {
  return chain === 'solana' ? a === b : a.toLowerCase() === b.toLowerCase();
}

export function useAuthBootstrap() {
  const { state, dispatch } = useAppContext();
  const evmSigner = useEthersSigner();
  const solanaSigner = useSolanaSigner();
  const { chain } = useAccount();
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [progress, setProgress] = useState('');
  const requestIdRef = useRef(0);
  const sessionRef = useRef({ chain: state.chain, walletAddress: state.walletAddress });

  useEffect(() => {
    sessionRef.current = { chain: state.chain, walletAddress: state.walletAddress };
    return () => {
      requestIdRef.current += 1;
    };
  }, [state.chain, state.walletAddress]);

  const verifyApiKey = useCallback(async (brokerId: string, accountId: string, orderlyKey: string, secretKey: Uint8Array) => {
    await retry(async () => {
      await api.getSettleNonce({ accountId, brokerId, orderlyKey, secretKey });
    }, { retries: 4, delayMs: 750 });
  }, []);

  const bootstrap = useCallback(async (brokerId: string) => {
    const activeChain = state.chain;
    if (!activeChain) throw new Error('Chain not detected');

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const signer = activeChain === 'solana' ? solanaSigner : evmSigner;

    if (!signer || !state.walletAddress) throw new Error('Wallet not connected');

    const activeWalletAddress = state.walletAddress;
    const isCurrentSession = () => (
      requestIdRef.current === requestId
      && sessionRef.current.chain === activeChain
      && sessionRef.current.walletAddress === activeWalletAddress
    );

    const chainId = activeChain === 'solana' ? config.solana.chainId : chain?.id;
    if (!chainId) throw new Error('Chain not detected');

    setStatus('loading');
    try {
      const address = await signer.getAddress();
      if (!addressesMatch(activeChain, address, activeWalletAddress)) {
        throw new Error('Wallet signer does not match the connected wallet address. Please reconnect and try again.');
      }

      // 1. Check if account exists
      setProgress('Checking account...');
      const chainType = activeChain === 'solana' ? 'SOL' : 'EVM';
      let accountInfo = await api.getAccount(address, brokerId, chainType);

      if (!accountInfo) {
        // Register account
        setProgress('Registering account — please sign in your wallet...');
        const { registration_nonce } = await api.getRegistrationNonce();
        const timestamp = Date.now();
        const regMessage = { brokerId, chainId, timestamp, registrationNonce: registration_nonce };
        const regSignature = activeChain === 'solana'
          ? await signSolanaRegistration(solanaSigner!, regMessage)
          : await signRegistration(evmSigner!, regMessage);
        const result = await api.registerAccount({
          message: regMessage,
          signature: regSignature,
          userAddress: address,
        });
        accountInfo = { account_id: result.orderly_account_id ?? result.account_id, user_id: 0 };
      }

      const accountId = accountInfo.account_id;

      // 2. Check for existing Ed25519 keypair
      let keyPair = loadKeyPair(brokerId, accountId);

      if (keyPair) {
        setProgress('Verifying API key...');
        await verifyApiKey(brokerId, accountId, `ed25519:${keyPair.publicKey}`, keyPair.secretKey);
      } else {
        setProgress('Adding API key — please sign in your wallet...');
        keyPair = generateKeyPair();
        const orderlyKey = `ed25519:${keyPair.publicKey}`;
        const timestamp = Date.now();
        const expiration = timestamp + 365 * 24 * 60 * 60 * 1000; // 1 year
        const chainType: OrderlyChainType = activeChain === 'solana' ? 'SOL' : 'EVM';
        const keyMessage = { brokerId, chainId, orderlyKey, scope: 'read,trading', timestamp, expiration, chainType };
        const keySignature = activeChain === 'solana'
          ? await signSolanaAddOrderlyKey(solanaSigner!, keyMessage)
          : await signAddOrderlyKey(evmSigner!, keyMessage);
        if (activeChain !== 'solana') {
          const recoveredSigner = recoverAddOrderlyKeySigner(keyMessage, keySignature);
          if (!addressesMatch(activeChain, recoveredSigner, address)) {
            throw new Error(`Orderly key signature recovered ${recoveredSigner}, expected ${address}. Please reconnect and try again.`);
          }
        }
        await api.addOrderlyKey({
          message: keyMessage,
          signature: keySignature,
          userAddress: address,
        });

        setProgress('Waiting for API key activation...');
        await verifyApiKey(brokerId, accountId, orderlyKey, keyPair.secretKey);

        saveKeyPair(brokerId, accountId, keyPair);
      }

      if (!isCurrentSession()) {
        if (requestIdRef.current === requestId) {
          setStatus('idle');
          setProgress('');
        }
        return;
      }

      // 3. Dispatch auth context
      const auth = {
        accountId,
        brokerId,
        orderlyKey: `ed25519:${keyPair.publicKey}`,
        secretKey: keyPair.secretKey,
      };
      dispatch({ type: 'SET_AUTH_CONTEXT', auth, walletAddress: activeWalletAddress, chain: activeChain });

      setStatus('success');
      setProgress('');
    } catch (err) {
      if (!isCurrentSession()) {
        if (requestIdRef.current === requestId) {
          setStatus('idle');
          setProgress('');
        }
        throw err;
      }

      setStatus('error');
      setProgress('');
      const msg = err instanceof Error ? err.message : 'Authentication failed';
      captureException(err, {
        tags: { feature: 'auth_bootstrap', chain: activeChain },
        extra: { brokerId, walletAddress: activeWalletAddress },
      });
      dispatch({ type: 'SET_ERROR', error: msg });
      throw err;
    }
  }, [evmSigner, solanaSigner, chain, state.chain, state.walletAddress, dispatch, verifyApiKey]);

  return { bootstrap, status, progress };
}
